import React from "react";
import { Row, Col } from "reactstrap";
import { Icon } from "@cadmakers-ui/icons";
import { showSnack } from "react-redux-snackbar";
import { isPhone } from "@cadmakers-ui/core";
import { ThunkDispatch } from "redux-thunk";
import { connect } from "react-redux";
import cx from "classnames";
import { FeedbackVote } from "../../../Constants/feedbackEnums";
import { endpoints } from "../../..";
import { ApiRequest, IAppState } from "../../../../Common";
import styles from "../../../Styles/VoteOnFeedbackButton.module.scss";
import { feedbackVoteService } from "../../../Services/FeedbackVoteService";
import iconStyles from "../../../../Common/Styles/Icons.module.scss";

type Props = {
  feedbackCode: string;
  existingVote?: string;
  voteCount?: number;
  onVote: (feedbackCode: string, vote: FeedbackVote) => void;
  vertical?: boolean;
};

type DispatchProps = {
  showSnack: (uniqueId: any, args: any) => void;
};

type State = {
  currentVote: FeedbackVote;
  voteCount: number;
};

function mapDispatchToProps(dispatch: ThunkDispatch<IAppState, {}, any>): DispatchProps {
  return {
    showSnack: (uniqueId: any, args: any) => dispatch(showSnack(uniqueId, args))
  };
}

class VoteOnFeedbackButton extends React.Component<Props & DispatchProps, State> {
  voteLock: boolean;

  constructor(props: Props & DispatchProps) {
    super(props);

    let parsedExistingVote: FeedbackVote = FeedbackVote.None;
    if (this.props.existingVote) {
      parsedExistingVote = FeedbackVote[this.props.existingVote as keyof typeof FeedbackVote];
    } else {
      parsedExistingVote = FeedbackVote.None;
    }

    this.state = {
      currentVote: parsedExistingVote,
      voteCount: this.props.voteCount ? this.props.voteCount : 0
    };

    this.voteLock = false;
  }

  onUpvote(e: React.MouseEvent<HTMLAnchorElement>) {
    this.onVote(this.props.feedbackCode, FeedbackVote.Up);

    e.stopPropagation();
  }

  onDownvote(e: React.MouseEvent<HTMLAnchorElement>) {
    this.onVote(this.props.feedbackCode, FeedbackVote.Down);

    e.stopPropagation();
  }

  onUndoVote(e: React.MouseEvent<HTMLAnchorElement>) {
    this.onVote(this.props.feedbackCode, FeedbackVote.None);

    e.stopPropagation();
  }

  onVote(feedbackCode: string, vote: FeedbackVote) {
    if (!this.claimVoteLock()) {
      return;
    }

    // POST vote to server
    const { onVote } = this.props;
    const setState = this.setState.bind(this);
    const releaseLock = this.releaseVoteLock.bind(this);

    const body = {
      NewVote: vote.toString()
    };

    ApiRequest.fromPath(this.constructVoteUrl(feedbackCode))
      .withJson(body)
      .withAuthHeader()
      .post()
      .then(response => {
        if (response.status === 200) {
          setState(
            state => {
              return {
                ...state,
                currentVote: vote,
                voteCount: feedbackVoteService.getNewCountAfterVoteHelper(
                  state.voteCount,
                  state.currentVote,
                  vote
                )
              };
            },
            () => {
              this.showPostVoteSnackbar(vote);
              onVote(feedbackCode, vote);
            }
          );
        } else {
          response.json().then(json => {
            this.showErrorSnackbar(response.status, json.title, vote);
          });
        }
        releaseLock();
      });
  }

  showPostVoteSnackbar(vote: FeedbackVote) {
    let label = "";

    if (vote === FeedbackVote.None) {
      label = "Vote successfully removed";
    } else {
      label = "Thanks for voting!";
    }
    this.props.showSnack("VOTE_ON_FEEDBACK", {
      label,
      timeout: 2000
    });
  }

  showErrorSnackbar(errorCode: number, errorDescription: string, vote: FeedbackVote) {
    let errorResult = "";

    if (vote === FeedbackVote.None) {
      errorResult = "Failed to remove vote";
    } else {
      errorResult = "Failed to vote";
    }
    this.props.showSnack("VOTE_ON_FEEDBACK", {
      label: `${errorResult}: ${errorDescription} (Error Code ${errorCode})`,
      timeout: 5000
    });
  }

  constructVoteUrl(feedbackCode: string) {
    return endpoints.feedback.vote(feedbackCode);
  }

  claimVoteLock() {
    if (!this.voteLock) {
      this.voteLock = true;
      return true;
    }
    return false;
  }

  releaseVoteLock() {
    this.voteLock = false;
  }

  render() {
    const upvoteButtonClass: string =
      this.state.currentVote === FeedbackVote.Up ? styles.upvoted : styles.notVoted;
    const downvoteButtonClass: string =
      this.state.currentVote === FeedbackVote.Down ? styles.downvoted : styles.notVoted;

    const onUpvoteClick =
      this.state.currentVote === FeedbackVote.Up
        ? this.onUndoVote.bind(this)
        : this.onUpvote.bind(this);
    const onDownvoteClick =
      this.state.currentVote === FeedbackVote.Down
        ? this.onUndoVote.bind(this)
        : this.onDownvote.bind(this);

    if (this.props.vertical) {
      return (
        <div className={cx(styles.vertical, { [styles.mobile]: isPhone })}>
          <div className={styles.verticalContainerDiv}>
            <a onClick={onUpvoteClick} className={styles.voteOnFeedbackLink}>
              <Icon
                appName="GeneralLayout"
                assetName="GENERAL_L_SortDnUp"
                className={cx(upvoteButtonClass, iconStyles.smallIcon)}
              />
            </a>
          </div>
          <div className={styles.verticalContainerDiv}>{this.state.voteCount}</div>
          <div className={styles.verticalContainerDiv}>
            <a onClick={onDownvoteClick} className={styles.voteOnFeedbackLink}>
              <Icon
                appName="GeneralLayout"
                assetName="GENERAL_L_SortUpDn"
                className={cx(downvoteButtonClass, iconStyles.smallIcon)}
              />
            </a>
          </div>
        </div>
      );
    }
    return (
      <>
        <div className={cx(styles.horizontal, { [styles.mobile]: isPhone })}>
          <Row>
            <Col colSpan={4} className={styles.horizontalCol}>
              <a onClick={onUpvoteClick} className={styles.voteOnFeedbackLink}>
                <Icon
                  appName="GeneralLayout"
                  assetName="GENERAL_L_SortDnUp"
                  className={cx(upvoteButtonClass, iconStyles.smallIcon)}
                />
              </a>
            </Col>
            <Col colSpan={4} className={styles.horizontalCol}>
              {this.state.voteCount}
            </Col>
            <Col colSpan={4} className={styles.horizontalCol}>
              <a onClick={onDownvoteClick} className={styles.voteOnFeedbackLink}>
                <Icon
                  appName="GeneralLayout"
                  assetName="GENERAL_L_SortUpDn"
                  className={cx(downvoteButtonClass, iconStyles.smallIcon)}
                />
              </a>
            </Col>
          </Row>
        </div>
      </>
    );
  }
}

export default connect(null, mapDispatchToProps)(VoteOnFeedbackButton);
